import React, { useEffect } from 'react';

const BAA_VERSION = '1.0';

export default function BAA() {
  const params = new URLSearchParams(window.location.search);
  const practice = params.get('practice') || '[PRACTICE NAME]';
  const accepted = params.get('accepted') || '[DATE OF ACCOUNT CREATION]';
  const autoPrint = params.get('print') === '1';
  const isFilled = params.has('practice');

  useEffect(() => {
    if (autoPrint) {
      const t = setTimeout(() => window.print(), 800);
      return () => clearTimeout(t);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ background: '#fff', minHeight: '100vh' }}>
      {/* Nav bar — hidden when printing */}
      <div className="no-print" style={{ background: '#111', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <a href="/" style={{ lineHeight: 0 }}>
          <img src="/assets/momorox-lockup.png" alt="MomoroX" style={{ height: '24px', width: 'auto' }} />
        </a>
        <button
          onClick={() => window.print()}
          style={{
            font: '500 13px "IBM Plex Mono",monospace',
            color: '#111',
            background: '#dcedc2',
            border: 'none',
            borderRadius: '2px',
            padding: '8px 16px',
            cursor: 'pointer',
          }}
        >
          Download / Print PDF
        </button>
      </div>

      {/* BAA Document */}
      <div style={{ maxWidth: '820px', margin: '0 auto', padding: '48px 32px 80px', fontFamily: "'Archivo', system-ui, sans-serif", color: '#111', lineHeight: 1.7 }}>

        <p style={{ font: '400 11px "IBM Plex Mono",monospace', color: '#6b7280', marginBottom: '8px', letterSpacing: '.08em' }}>
          HIPAA COMPLIANT · VERSION {BAA_VERSION}
        </p>
        <h1 style={{ fontSize: '26px', fontWeight: 700, marginBottom: '4px' }}>Business Associate Agreement</h1>
        <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '40px' }}>
          This agreement is between Home Logic, LLC (dba MomoroX) and the dental practice identified below.
        </p>

        {isFilled && (
          <div style={{ background: '#f3f9e9', border: '1px solid #dcedc2', borderRadius: '6px', padding: '16px 20px', marginBottom: '32px', fontSize: '14px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div><span style={{ font: '500 11px "IBM Plex Mono",monospace', color: '#6b7280', display: 'block', marginBottom: '2px' }}>COVERED ENTITY</span>{practice}</div>
              <div><span style={{ font: '500 11px "IBM Plex Mono",monospace', color: '#6b7280', display: 'block', marginBottom: '2px' }}>EFFECTIVE DATE</span>{accepted}</div>
            </div>
          </div>
        )}

        <Section title="Parties">
          <p>This Business Associate Agreement ("<strong>Agreement</strong>") is entered into as of <strong>{accepted}</strong> ("<strong>Effective Date</strong>") between:</p>
          <p><strong>Business Associate:</strong> Home Logic, LLC, a Pennsylvania limited liability company, doing business as MomoroX ("<strong>Business Associate</strong>" or "<strong>MomoroX</strong>"), with its principal place of business at 1898 Berue Dr, Coatesville, PA 19320; and</p>
          <p><strong>Covered Entity:</strong> <strong>{practice}</strong> ("<strong>Covered Entity</strong>" or "<strong>Practice</strong>"), as registered in the MomoroX platform on the Effective Date.</p>
        </Section>

        <Section title="Recitals">
          <p>WHEREAS, Business Associate provides dental practice management software, AI-powered patient communication, appointment scheduling, and related services ("<strong>Services</strong>") to Covered Entity;</p>
          <p>WHEREAS, in the course of providing the Services, Business Associate may create, receive, maintain, or transmit Protected Health Information ("<strong>PHI</strong>") on behalf of Covered Entity;</p>
          <p>WHEREAS, both parties desire to protect the privacy and provide for the security of PHI in compliance with the Health Insurance Portability and Accountability Act of 1996 ("<strong>HIPAA</strong>"), as amended by the Health Information Technology for Economic and Clinical Health Act of 2009 ("<strong>HITECH</strong>"), and the regulations promulgated thereunder (collectively, "<strong>HIPAA Rules</strong>");</p>
          <p>NOW, THEREFORE, in consideration of the mutual promises and covenants set forth herein, and for good and valuable consideration, the receipt and sufficiency of which are acknowledged, the parties agree as follows:</p>
        </Section>

        <Section title="Article 1 — Definitions">
          <Def term="1.1 Breach">"Breach" has the meaning set forth in 45 C.F.R. § 164.402.</Def>
          <Def term="1.2 Business Associate">"Business Associate" has the meaning set forth in 45 C.F.R. § 160.103.</Def>
          <Def term="1.3 Covered Entity">"Covered Entity" has the meaning set forth in 45 C.F.R. § 160.103.</Def>
          <Def term="1.4 Data Aggregation">"Data Aggregation" has the meaning set forth in 45 C.F.R. § 164.501.</Def>
          <Def term="1.5 Designated Record Set">"Designated Record Set" has the meaning set forth in 45 C.F.R. § 164.501.</Def>
          <Def term="1.6 ePHI">"Electronic Protected Health Information" or "ePHI" has the meaning set forth in 45 C.F.R. § 160.103.</Def>
          <Def term="1.7 HIPAA Rules">"HIPAA Rules" means the Privacy, Security, Breach Notification, and Enforcement Rules at 45 C.F.R. Parts 160 and 164.</Def>
          <Def term="1.8 Individual">"Individual" has the meaning set forth in 45 C.F.R. § 160.103.</Def>
          <Def term="1.9 PHI">"Protected Health Information" or "PHI" has the meaning set forth in 45 C.F.R. § 160.103, limited to information created, received, maintained, or transmitted by Business Associate on behalf of Covered Entity.</Def>
          <Def term="1.10 Required By Law">"Required By Law" has the meaning set forth in 45 C.F.R. § 164.103.</Def>
          <Def term="1.11 Secretary">"Secretary" means the Secretary of the U.S. Department of Health and Human Services or his/her designee.</Def>
          <Def term="1.12 Security Incident">"Security Incident" has the meaning set forth in 45 C.F.R. § 164.304.</Def>
          <Def term="1.13 Subcontractor">"Subcontractor" has the meaning set forth in 45 C.F.R. § 160.103.</Def>
          <Def term="1.14 Unsecured PHI">"Unsecured PHI" has the meaning set forth in 45 C.F.R. § 164.402.</Def>
        </Section>

        <Section title="Article 2 — Obligations of Business Associate">
          <SubSection title="2.1 Permitted Uses and Disclosures">
            Business Associate may use or disclose PHI only: (a) as necessary to perform the Services; (b) as Required By Law; (c) as otherwise permitted under this Agreement; or (d) as permitted by the Privacy Rule where Covered Entity is also permitted to make such use or disclosure.
          </SubSection>
          <SubSection title="2.2 Prohibited Uses and Disclosures">
            Business Associate shall not: (a) use or disclose PHI in a manner that would violate the HIPAA Rules if done by Covered Entity; (b) use or disclose PHI for fundraising or marketing without authorization; (c) sell PHI without authorization; or (d) use PHI in a manner that violates the minimum necessary standard.
          </SubSection>
          <SubSection title="2.3 Safeguards">
            Business Associate shall implement and maintain appropriate administrative, physical, and technical safeguards to protect PHI, including safeguards required by the Security Rule (45 C.F.R. Part 164, Subpart C) to protect ePHI.
          </SubSection>
          <SubSection title="2.4 Reporting">
            Business Associate shall: (a) report to Covered Entity any unauthorized use or disclosure of PHI within fifteen (15) calendar days of discovery; (b) report any Breach of Unsecured PHI within sixty (60) calendar days of discovery, per 45 C.F.R. § 164.410; and (c) report any Security Incident of which Business Associate becomes aware. All reports shall be sent to Covered Entity's notification email on file.
          </SubSection>
          <SubSection title="2.5 Subcontractors">
            Business Associate shall ensure any Subcontractor that creates, receives, maintains, or transmits PHI on behalf of Business Associate agrees to the same restrictions and conditions by entering into a written agreement compliant with 45 C.F.R. §§ 164.502(e) and 164.308(b).
          </SubSection>
          <SubSection title="2.6 Approved Subcontractors (Subprocessors)">
            <p>The following Subcontractors are authorized to process PHI in connection with the Services:</p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginTop: '8px' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <Th>Subcontractor</Th>
                  <Th>Role</Th>
                  <Th>Location</Th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <Td>Google LLC (Google Cloud Platform, Firebase, Vertex AI)</Td>
                  <Td>Cloud infrastructure, database, authentication, AI processing</Td>
                  <Td>United States</Td>
                </tr>
                <tr style={{ background: '#f9fafb' }}>
                  <Td>Resend Inc.</Td>
                  <Td>Transactional email notifications (patient name, contact info)</Td>
                  <Td>United States</Td>
                </tr>
                <tr>
                  <Td>Open Dental Software, Inc.</Td>
                  <Td>Practice management system integration (only if enabled by Covered Entity)</Td>
                  <Td>United States</Td>
                </tr>
              </tbody>
            </table>
          </SubSection>
          <SubSection title="2.7 Individual Access">
            To the extent Business Associate maintains PHI in a Designated Record Set, Business Associate shall make PHI available to Covered Entity to allow Covered Entity to fulfill its obligations under 45 C.F.R. § 164.524.
          </SubSection>
          <SubSection title="2.8 Amendment">
            To the extent Business Associate maintains PHI in a Designated Record Set, Business Associate shall make such PHI available for amendment and incorporate any amendments per 45 C.F.R. § 164.526.
          </SubSection>
          <SubSection title="2.9 Accounting of Disclosures">
            Business Associate shall maintain and make available information necessary for Covered Entity to provide an accounting of disclosures per 45 C.F.R. § 164.528.
          </SubSection>
          <SubSection title="2.10 HHS Inspection">
            Business Associate shall make its internal practices, books, and records relating to PHI available to the Secretary for purposes of determining Covered Entity's compliance with the HIPAA Rules.
          </SubSection>
          <SubSection title="2.11 Minimum Necessary">
            Business Associate shall limit its use and disclosure of PHI to the minimum necessary to accomplish the intended purpose of the Services.
          </SubSection>
        </Section>

        <Section title="Article 3 — Permitted Uses by Business Associate">
          <SubSection title="3.1 Management and Administration">
            Business Associate may use PHI for its proper management and administration or to carry out its legal responsibilities, provided that any disclosure is Required By Law, or Business Associate obtains reasonable assurances from the recipient that the information will be held confidentially.
          </SubSection>
          <SubSection title="3.2 Data Aggregation">
            Business Associate may use PHI to provide Data Aggregation services relating to the health care operations of Covered Entity.
          </SubSection>
          <SubSection title="3.3 De-Identified Data">
            Business Associate may de-identify PHI in accordance with 45 C.F.R. § 164.514(a)–(c) and use such de-identified data for any lawful purpose, including service improvement and analytics.
          </SubSection>
        </Section>

        <Section title="Article 4 — Obligations of Covered Entity">
          <p>Covered Entity shall: (a) notify Business Associate of any limitations in Covered Entity's Notice of Privacy Practices that would affect Business Associate's use or disclosure of PHI; (b) notify Business Associate of any changes in, or revocations of, permission by an Individual to use or disclose PHI that affect Business Associate's permitted uses or disclosures; (c) not request Business Associate to use or disclose PHI in any manner that would not be permissible under the HIPAA Rules if done by Covered Entity; and (d) obtain any consents or authorizations required by applicable law prior to furnishing Business Associate with PHI relating to minors, substance abuse, mental health, HIV/AIDS, or other specially protected categories of health information.</p>
        </Section>

        <Section title="Article 5 — Term and Termination">
          <SubSection title="5.1 Term">
            This Agreement is effective as of the Effective Date and continues until terminated as provided herein or until the service relationship between the parties ends, whichever occurs first.
          </SubSection>
          <SubSection title="5.2 Termination for Cause">
            Upon knowledge of a material breach by Business Associate, Covered Entity shall notify Business Associate and provide a reasonable opportunity (not less than thirty (30) days) to cure. If Business Associate fails to cure, Covered Entity may terminate this Agreement and the Services.
          </SubSection>
          <SubSection title="5.3 Effect of Termination">
            Upon termination, Business Associate shall return or destroy all PHI received from or created on behalf of Covered Entity, retaining no copies. If return or destruction is infeasible, Business Associate shall extend the protections of this Agreement to the retained PHI and limit further use or disclosure to those purposes that make return or destruction infeasible.
          </SubSection>
        </Section>

        <Section title="Article 6 — Miscellaneous">
          <SubSection title="6.1 Regulatory References">
            References to HIPAA Rules sections mean those sections as in effect or as amended.
          </SubSection>
          <SubSection title="6.2 Amendment">
            Business Associate may update this Agreement to maintain compliance with applicable law by providing thirty (30) days' written notice to Covered Entity's email on file. The updated terms shall be binding unless Covered Entity terminates the Services prior to the effective date of the update.
          </SubSection>
          <SubSection title="6.3 Interpretation">
            This Agreement shall be interpreted broadly as necessary to implement and comply with the HIPAA Rules. Any ambiguity shall be resolved in favor of a meaning that permits Covered Entity to comply with the HIPAA Rules.
          </SubSection>
          <SubSection title="6.4 Survival">
            The obligations of Business Associate under Section 5.3 (Effect of Termination) survive termination of this Agreement.
          </SubSection>
          <SubSection title="6.5 Entire Agreement">
            This Agreement constitutes the entire agreement between the parties regarding its subject matter and supersedes all prior discussions, negotiations, and agreements with respect thereto.
          </SubSection>
          <SubSection title="6.6 Governing Law">
            This Agreement is governed by the laws of the Commonwealth of Pennsylvania, without regard to conflict of law principles, except to the extent superseded by federal law.
          </SubSection>
          <SubSection title="6.7 Electronic Acceptance">
            This Agreement may be accepted electronically. An electronic click-through acceptance of these terms during account creation constitutes a legally binding signature for all purposes and is enforceable to the same extent as a handwritten signature under the Electronic Signatures in Global and National Commerce Act (E-SIGN Act) and applicable state law.
          </SubSection>
        </Section>

        {/* Signature block */}
        <div style={{ borderTop: '2px solid #111', paddingTop: '32px', marginTop: '40px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', fontSize: '14px' }}>
          <div>
            <p style={{ fontWeight: 600, marginBottom: '8px' }}>BUSINESS ASSOCIATE</p>
            <p>Home Logic, LLC (dba MomoroX)</p>
            <p>1898 Berue Dr, Coatesville, PA 19320</p>
            <p style={{ marginTop: '8px' }}>By: Nick Makarov, Authorized Representative</p>
          </div>
          <div>
            <p style={{ fontWeight: 600, marginBottom: '8px' }}>COVERED ENTITY</p>
            <p>{practice}</p>
            {isFilled
              ? <p style={{ marginTop: '8px' }}>Electronically accepted on <strong>{accepted}</strong></p>
              : <p style={{ marginTop: '8px', color: '#6b7280' }}>Electronically accepted on account creation</p>
            }
          </div>
        </div>

        <p style={{ marginTop: '48px', font: '400 11px "IBM Plex Mono",monospace', color: '#9ca3af', textAlign: 'center' }}>
          MomoroX BAA v{BAA_VERSION} · Home Logic, LLC · questions: legal@momorox.com
        </p>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
          @page { margin: 1in; }
        }
      `}</style>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '32px' }}>
      <h2 style={{ fontSize: '15px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px', marginBottom: '16px' }}>
        {title}
      </h2>
      <div style={{ fontSize: '14px' }}>{children}</div>
    </div>
  );
}

function SubSection({ title, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <p style={{ fontWeight: 600, marginBottom: '4px' }}>{title}</p>
      <div style={{ color: '#2a2a2a' }}>{children}</div>
    </div>
  );
}

function Def({ term, children }) {
  return (
    <p style={{ marginBottom: '8px' }}>
      <strong>{term}. </strong>{children}
    </p>
  );
}

function Th({ children }) {
  return <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>{children}</th>;
}

function Td({ children }) {
  return <td style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' }}>{children}</td>;
}
